<template>
  <div id="po-table-container">
    <div class="po-header-container">
      <div class="po-header">
        Details
      </div>
      <div class="flex-row">
        <SACTableSearch
          :search="poTableSearch"
          @input="changeSearch"
        />
        <SACSettingMenu
          :options="[{
            label: 'New Order',
            action: () => toggleActionModal(true, 'order'),
          }, {
            label: 'New Invoice',
            action: () => toggleActionModal(true, 'partner-order'),
          }]"
        />
      </div>
    </div>
    <div class="po-container">
      <v-data-table
        :headers="headers"
        :items="poTableData"
        :loading="loadingOrder"
        :options="poTableOptions"
        :search="poTableSearch"
        height="700px"
        fixed-header
        hide-default-footer
      >
        <template #item.orderNumber="{ item }">
          <td>
            <div class="o-status-container">
              <span
                v-if="determineOrderStatusDot(item) !== 'error'
                  && determineOrderStatusDot(item) !== 'cancelled'"
                :class="[determineOrderStatusDot(item), 'dot']"
              />
              <span
                v-else-if="determineOrderStatusDot(item) === 'error'"
                class="error-status"
              >
                !
              </span>
              <span
                v-else-if="determineOrderStatusDot(item) === 'cancelled'"
                class="cancelled-status"
              >
                -
              </span>
              <router-link :to="`/order/partner/${item.orderNumber}`">
                {{ item.orderNumber }}
              </router-link>
            </div>
          </td>
        </template>
        <template #item.status="{ item }">
          <td>
            <span :class="[determineOrderStatus(item.status)]">
              {{ item.status }}
            </span>
          </td>
        </template>
        <template #item.orderDate="{ item }">
          <td>
            {{ customDateFilter(item.orderDate, dateFormat) }}
          </td>
        </template>
        <template #item.shipDate="{ item }">
          <td>
            {{ customDateFilter(item.shipDate, dateFormat) }}
          </td>
        </template>
        <template #item.deliveryDate="{ item }">
          <td>
            {{ customDateFilter(item.deliveryDate, dateFormat) }}
          </td>
        </template>
        <template #[`item.requestorId`]="{ item }">
          <td>
            <div v-if="item.requestorId && item.requestorId > 0">
              {{ item.requestorFirstName }} {{ item.requestorLastName }}
            </div>
            <div v-else>
              --
            </div>
          </td>
        </template>
        <template #item.trackingNumber="{ item }">
          <td>
            <a
              v-if="item.trackingNumber"
              :href="createTrackingLink(item)"
              target="_blank"
            >
              {{ item.trackingNumber }}
            </a>
            <span v-else>
              --
            </span>
          </td>
        </template>
        <template #item.campaignCode="{ item }">
          <td>
            {{ item.campaignCode || "--" }}
          </td>
        </template>
        <template #item.isPilot="{ item }">
          <td>
            {{ item.isPilot ? 'Yes' : 'No' }}
          </td>
        </template>
      </v-data-table>
    </div>
    <SACTableFooter
      :totalRecords="poTableData.length"
      :rowCount="poTableOptions.itemsPerPage"
      :changeRowCount="changeRowCount"
      :page="poTableOptions.page"
      :totalPage="poTableRecordCount"
      :changePage="changePage"
      :loading="loadingOrder"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import SACSettingMenu from '@shared/components/sac/SACSettingMenu.vue';
import SACTableSearch from '@shared/components/table/SACTableSearch.vue';
import SACTableFooter from '@shared/components/table/SACTableFooter.vue';

import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import partnerDetailMixin from '@shared/mixins/partnerDetailMixin';

export default {
  name: 'OrderTable',
  components: {
    SACSettingMenu,
    SACTableSearch,
    SACTableFooter,
  },
  mixins: [partnerDetailMixin, unitPreferencesMixin],
  data() {
    return {
      headers: [{
        text: 'Order Number',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'orderNumber',
      }, {
        text: 'Order Status',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'status',
      }, {
        text: 'Order Date',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'orderDate',
      }, {
        text: 'Ship Date',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'shipDate',
      }, {
        text: 'Delivery Date',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'deliveryDate',
      }, {
        text: 'Tracking Number',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'trackingNumber',
      }, {
        text: 'Campaign',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'campaignCode',
      }, {
        text: 'Pilot',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'isPilot',
      },
      {
        text: 'Requested By',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'requestorId',
      },
      ],
    };
  },
  computed: {
    ...mapState({
      poTableSearch: (state) => state.partnerDetail.poTableSearch,
      poTableOptions: (state) => state.partnerDetail.poTableOptions,
    }),
    ...mapGetters([
      'poTableRecordCount',
      'poTableData',
    ]),
  },
  methods: {
    changePage(page) {
      this.$store.dispatch('changePOTableOption', {
        field: 'page',
        value: page,
      });
    },
    changeRowCount(rowCount) {
      this.$store.dispatch('changePOTableOption', {
        field: 'itemsPerPage',
        value: rowCount,
      });
    },
    changeSort([item]) {
      this.$store.dispatch('changePOTableOption', {
        field: 'sortBy',
        value: [item],
      });
    },
    changeSearch(value) {
      this.$store.dispatch('changePOTableOption', {
        field: 'poTableSearch',
        value,
      });
    },
  },
};
</script>

<style lang="scss">
;

#po-table-container {
  width: 100%;
  padding: 20px;

  .po-header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;

    .po-header {
      font-size: 1.5625rem;
      color: $N100;
      font-weight: 700;
    }
  }

  .po-container {
    background-color: $WHITE;
    box-shadow: 0 0 7px 2px rgba(9, 15, 32, 0.02);
    border-radius: 6px;
    padding-bottom: 12px;

    .v-data-table__wrapper {
      height: 700px;

      thead {
        tr {
          th {
            background-color: $N100;
            color: $WHITE;
            font-size: 15px;
            font-weight: 500;
            text-transform: uppercase;

            .mdi-checkbox-blank-outline::before,
            .mdi-checkbox-marked::before,
            .mdi-minus-box::before,
            .mdi-arrow-up::before {
              color: $WHITE;
            }

            &:first-child {
              border-top-left-radius: 6px;
            }

            &:last-child {
              border-top-right-radius: 6px;
            }

            &.sortable {
              i {
                display: none;
              }
            }

            &.active {
              i {
                display: inline-block;
              }
            }
          }
        }
      }

      tbody {
        tr {
          td {
            border-bottom: 0;

            .mdi-checkbox-marked::before {
              color: $NBLUE100;
            }

            .edit-btn {
              background: $N90;
              color: $WHITE;
              width: 70px;
              padding: 3px;
            }
          }

          &:hover {
            background: none;
          }

          &:nth-child(even) {
            background-color: $NBLUE5;
          }
        }
      }
    }
  }

  .o-status-container {
    display: flex;
    align-items: center;
  }

  .green-dot {
    background-color: $BASIL100;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
  }

  .red-dot {
    background-color: $TOMATO100;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
  }

  .yellow-dot {
    background-color: $SUNSET100;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
  }

  .error-status {
    width: 8px;
    margin-right: 12px;
    text-align: center;
    color: $TOMATO100;
    font-weight: 700;
    font-size: 1rem;
    transform: scale(1.2);
  }

  .cancelled-status {
    width: 8px;
    margin-right: 12px;
    text-align: center;
    color: $BLACK;
    font-weight: 700;
    font-size: 1rem;
    transform: scale(1.2);
  }
}
</style>
